
import { unzipSync, strFromU8, type Unzipped } from "fflate";
import { resolveThemeChain } from "./ooxmlPaths";
import { COMMON_FONTS, NS_A } from "../../../const";
import { getActivePresentationBuffer } from "../../../utils/officePptx";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { logOfficeError } from "../../../../../telemetry";

export interface FontOption {
  name: string;
  /** "Title" | "Body" | "PPT" | "" — shown as a datalist hint. */
  tag: string;
}

export interface FontData {
  themeFonts: { majorFont: string | null; minorFont: string | null };
  usedFonts: string[];
  allFonts: FontOption[];
}

let _cached: FontData | null = null;

/** Load (and cache) the combined font list for the active presentation. */
export async function loadFontData(): Promise<FontData> {
  if (_cached) return _cached;
  try {
    const buf = await getActivePresentationBuffer();
    const zip = unzipSync(new Uint8Array(buf));

    const themeFonts = extractThemeFonts(zip);
    const usedFonts = extractUsedFonts(zip);
    _cached = { themeFonts, usedFonts, allFonts: buildFontList(themeFonts, usedFonts) };
    return _cached;
  } catch {
    // Non-critical: fall back to the common font list if the deck can't be read.
    _cached = {
      themeFonts: { majorFont: null, minorFont: null },
      usedFonts: [],
      allFonts: COMMON_FONTS.map((name) => ({ name, tag: "" })),
    };
    return _cached;
  }
}

/** Clear the cached font list (call when the deck may have changed). */
export function clearFontDataCache(): void {
  _cached = null;
}

/** The font of the first selected shape, or null if none/untyped. */
export async function detectSelectedFont(): Promise<string | null> {
  if (!hasPpt()) return null; // not in PowerPoint — no selection to read (expected, not an error)
  let detected: string | null = null;
  try {
    await ppt().run(async (context) => {
      const sel = context.presentation.getSelectedShapes();
      sel.load("items");
      await context.sync();
      const first = sel.items?.[0];
      if (!first) return;
      const font = first.textFrame.textRange.font;
      font.load("name");
      await context.sync();
      detected = font.name || null;
    });
  } catch {
    /* shape may have no text frame (image, etc.) */
  }
  return detected;
}

/* ── OOXML parsing ──────────────────────────────────────────────── */

/** Theme major/minor Latin typefaces from the active slide's theme. */
function extractThemeFonts(zip: Unzipped): { majorFont: string | null; minorFont: string | null } {
  const themePath = resolveActiveThemePath(zip) ?? "ppt/theme/theme1.xml";
  const file = zip[themePath];
  if (!file) return { majorFont: null, minorFont: null };

  const doc = new DOMParser().parseFromString(strFromU8(file), "text/xml");
  const scheme = doc.getElementsByTagNameNS(NS_A, "fontScheme")[0];
  if (!scheme) return { majorFont: null, minorFont: null };

  const latinOf = (tag: string): string | null => {
    const el = scheme.getElementsByTagNameNS(NS_A, tag)[0];
    const latin = el?.getElementsByTagNameNS(NS_A, "latin")[0];
    return latin?.getAttribute("typeface") || null;
  };
  return { majorFont: latinOf("majorFont"), minorFont: latinOf("minorFont") };
}

/** Every Latin/complex-script/east-asian typeface used across the deck. */
function extractUsedFonts(zip: Unzipped): string[] {
  const fonts = new Set<string>();
  const re = /<a:(?:latin|cs|ea)\b[^>]*\btypeface="([^"]+)"/g;
  for (const path of Object.keys(zip)) {
    if (!/^ppt\/(slides|slideMasters|slideLayouts)\/[^/]+\.xml$/.test(path)) continue;
    const xml = strFromU8(zip[path]!);
    for (let m = re.exec(xml); m; m = re.exec(xml)) {
      const tf = m[1]!;
      if (tf && !tf.startsWith("+")) fonts.add(tf);
    }
  }
  return [...fonts].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/**
 * Theme first (tagged), then deck-used fonts, then the common fallback —
 * deduped case-insensitively so each font appears once.
 */
function buildFontList(
  themeFonts: { majorFont: string | null; minorFont: string | null },
  usedFonts: string[],
): FontOption[] {
  const out: FontOption[] = [];
  const seen = new Set<string>();
  const add = (name: string | null, tag: string): void => {
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ name, tag });
  };

  add(themeFonts.majorFont, "Title");
  add(themeFonts.minorFont, "Body");
  for (const f of usedFonts) add(f, "PPT");
  for (const f of COMMON_FONTS) add(f, "");
  return out;
}

/* ── Theme-path resolution (slide → layout → master → theme) ────── */

/** The active deck's theme path (slide 1's chain), or null when unresolved. */
function resolveActiveThemePath(zip: Unzipped): string | null {
  const slides = Object.keys(zip)
    .filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p))
    .sort((a, b) => Number.parseInt(/slide(\d+)/.exec(a)![1]!, 10) - Number.parseInt(/slide(\d+)/.exec(b)![1]!, 10));
  const slideFile = slides[0]?.split("/").pop();
  if (!slideFile) return null; // deck has no slides — expected, not an error
  return resolveThemeChain(zip, slideFile).themePath;
}

// ─── Installed-font check ──────────────────────────────────────────────────
// Best-effort "is this font installed on this device?" check, used for the
// font warnings (BRD EC-11, EC-12, EC-13). The page can't list system fonts,
// so it measures text in the font against a generic fallback: if the width
// differs from every fallback, the font rendered, so it is installed.

const SAMPLE = "mmmmmmmmmmlliWW@#0123";
const FALLBACKS = ["monospace", "serif", "sans-serif"] as const;
const cache = new Map<string, boolean>();

let context: CanvasRenderingContext2D | null | undefined;

function getContext(): CanvasRenderingContext2D | null {
  if (context !== undefined) return context;
  try {
    context = document.createElement("canvas").getContext("2d");
  } catch (error) {
    logOfficeError(error, "font-installed-canvas");
    context = null;
  }
  return context ?? null;
}

function widthOf(ctx: CanvasRenderingContext2D, font: string): number {
  ctx.font = `72px ${font}`;
  return ctx.measureText(SAMPLE).width;
}

/** Generic CSS families are always available. When canvas measurement isn't
 *  possible (e.g. a headless host) the font is assumed installed so no
 *  false warning blocks or alarms the user. */
export function isFontInstalled(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) return true;
  const cached = cache.get(trimmed.toLowerCase());
  if (cached !== undefined) return cached;

  const ctx = getContext();
  let installed = true;
  if (ctx) {
    const quoted = `"${trimmed.replace(/"/g, "")}"`;
    installed = FALLBACKS.some((fallback) => widthOf(ctx, `${quoted}, ${fallback}`) !== widthOf(ctx, fallback));
  }
  cache.set(trimmed.toLowerCase(), installed);
  return installed;
}

/** Fonts from `names` that aren't installed, de-duplicated, order preserved. */
export function findMissingFonts(names: readonly string[]): string[] {
  const seen = new Set<string>();
  const missing: string[] = [];
  for (const name of names) {
    const key = name.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    if (!isFontInstalled(name)) missing.push(name);
  }
  return missing;
}

/** "Calibri" / "Calibri and Aptos" / "A, B and C" for warning copy. */
export function formatFontList(fonts: readonly string[]): string {
  if (fonts.length <= 1) return fonts[0] ?? "";
  return `${fonts.slice(0, -1).join(", ")} and ${fonts[fonts.length - 1]}`;
}

/** Test hook: forget cached results. */
export function clearFontInstalledCache(): void {
  cache.clear();
  context = undefined;
}
